import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';
import CopSidebar from './CopSidebar';
import CopTopbar from './CopTopbar';
import { Toast } from './ui';
import {
	initialApprovals,
	initialAuditEvents,
	segments as seedSegments,
	seedExperiments,
	makeCustomSegment,
	makeCustomExperiment,
	initialScheduledReports,
	DEFAULT_CONFIDENCE,
	DEFAULT_SUPPRESSIONS,
	DEFAULT_BRAND_GUIDE,
	DEFAULT_ALLOCATION_MODE,
	DEFAULT_EXPERIMENT_LENGTH_DAYS,
	DEFAULT_CONFIDENCE_PCT,
} from './copData';
import Home from './views/Home';
import CommandCenter from './views/CommandCenter';
import ClusteringStudio from './views/ClusteringStudio';
import CampaignBuilder from './views/CampaignBuilder';
import ExperimentLab from './views/ExperimentLab';
import ExitIntelligence from './views/ExitIntelligence';
import IntelligenceReporting from './views/IntelligenceReporting';
import DeepResearch from './views/DeepResearch';
import ApprovalQueue from './views/ApprovalQueue';
import AuditLogView from './views/AuditLog';
import GlobalSuppressions from './views/GlobalSuppressions';
import Settings from './views/Settings';
import Help from './views/Help';
import { makeCopBackend } from './copApi';
import './cop.css';

// Shared in-memory store. Approval decisions and builder activations flow
// through here so every view (badges, audit, queue) stays consistent.
// This is the seam that later swaps to /api/v1/cop/* + socket updates.
const CopContext = createContext(null);
export const useCop = () => useContext(CopContext);

const VIEWS = {
	home: Home,
	'command-center': CommandCenter,
	clustering: ClusteringStudio,
	builder: CampaignBuilder,
	'experiment-lab': ExperimentLab,
	'exit-intelligence': ExitIntelligence,
	reporting: IntelligenceReporting,
	'deep-research': DeepResearch,
	approvals: ApprovalQueue,
	'audit-log': AuditLogView,
	suppressions: GlobalSuppressions,
	settings: Settings,
	help: Help,
};

const REPORTING_DATE_VIEWS = new Set(['command-center', 'clustering', 'experiment-lab', 'exit-intelligence', 'reporting', 'deep-research', 'audit-log']);

// Exported so the Settings page can offer the same default date-range choices.
export const REPORT_DATE_PRESETS = [
	{ value: 'last_7', label: 'Last 7 days' },
	{ value: 'last_30', label: 'Last 30 days' },
	{ value: 'last_90', label: 'Last 90 days' },
	{ value: 'month_to_date', label: 'Month to date' },
	{ value: 'quarter_to_date', label: 'Quarter to date' },
	{ value: 'custom', label: 'Custom dates' },
];

function toInputDate(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

function fromInputDate(value) {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, month - 1, day);
}

function addDays(date, days) {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
}

function startOfQuarter(date) {
	const month = Math.floor(date.getMonth() / 3) * 3;
	return new Date(date.getFullYear(), month, 1);
}

function makeReportDateRange(preset = 'last_90') {
	const end = new Date();
	end.setHours(0, 0, 0, 0);
	let start = addDays(end, -89);

	if (preset === 'last_7') start = addDays(end, -6);
	if (preset === 'last_30') start = addDays(end, -29);
	if (preset === 'month_to_date') start = new Date(end.getFullYear(), end.getMonth(), 1);
	if (preset === 'quarter_to_date') start = startOfQuarter(end);

	return {
		preset,
		startDate: toInputDate(start),
		endDate: toInputDate(end),
	};
}

function formatInputDate(value) {
	if (!value) return '';
	return fromInputDate(value).toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

function formatReportDateRange(range) {
	const preset = REPORT_DATE_PRESETS.find((p) => p.value === range.preset);
	const dates = `${formatInputDate(range.startDate)} - ${formatInputDate(range.endDate)}`;
	return range.preset === 'custom' || !preset ? dates : `${preset.label} (${dates})`;
}

export default function CopApp({ user, initialViewMode }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const view = searchParams.get('view') || 'command-center';
	const builderSegment = searchParams.get('segment') || null;

	// Approval + audit data flows through one backend (the COP data seam):
	// the live server-backed adapter (shared AOP store, /api/v1/cop/*) when the
	// user is authenticated, else the in-memory mock for the serverless preview.
	// Both expose the same async surface; CopApp mirrors their snapshots in state.
	const backend = useRef(null);
	if (!backend.current) backend.current = makeCopBackend({ seed: { approvals: initialApprovals, auditEvents: initialAuditEvents } });
	const [approvals, setApprovals] = useState([]);
	const [auditEvents, setAuditEvents] = useState([]);

	// Initial load (await resolves immediately for the mock, fetches for live).
	useEffect(() => {
		let cancelled = false;
		Promise.all([backend.current.listApprovals(), backend.current.listAudit()])
			.then(([a, ev]) => {
				if (cancelled) return;
				setApprovals(a);
				setAuditEvents(ev);
			})
			.catch(() => {
				/* leave empty on load failure; views render their empty states */
			});
		return () => {
			cancelled = true;
		};
	}, []);
	// Clustering Studio groups and Experiment Lab experiments live here so create/
	// update/delete stay consistent across the audit log, builder picker, and badges.
	// Recommendation-driven records are system-managed and refuse mutation.
	const [segments, setSegments] = useState(seedSegments);
	const [experiments, setExperiments] = useState(seedExperiments);
	const segmentSeq = useRef(0);
	const experimentSeq = useRef(0);
	const [autopilot, setAutopilot] = useState(true);
	// ---- Settings (configured on the Settings page) ----
	const [autopilotRiskCeiling, setAutopilotRiskCeiling] = useState('low');
	const [confidence, setConfidence] = useState(DEFAULT_CONFIDENCE);
	const [scheduledReports, setScheduledReports] = useState(initialScheduledReports);
	const [suppressions, setSuppressions] = useState(DEFAULT_SUPPRESSIONS);
	const [brandGuide, setBrandGuide] = useState(DEFAULT_BRAND_GUIDE);
	const [defaultAllocationMode, setDefaultAllocationMode] = useState(DEFAULT_ALLOCATION_MODE);
	const [defaultExperimentLength, setDefaultExperimentLength] = useState(DEFAULT_EXPERIMENT_LENGTH_DAYS);
	const [defaultConfidencePct, setDefaultConfidencePct] = useState(DEFAULT_CONFIDENCE_PCT);
	// The default reporting date range seeds every reporting view; changing it on
	// the Settings page re-applies it to the live range immediately.
	const [defaultDatePreset, setDefaultDatePreset] = useState('last_90');
	const [reportDateRange, setReportDateRange] = useState(() => makeReportDateRange('last_90'));
	// Internal View shows ops telemetry, warnings, and AOP links in amber
	// internal-only cards; Client View is exactly what an account sees in
	// Client Reporting — internal content never renders there.
	const [viewMode, setViewMode] = useState(() => {
		if (initialViewMode === 'client' || initialViewMode === 'internal') return initialViewMode;
		return searchParams.get('mode') === 'client' ? 'client' : 'internal';
	});
	const [theme, setTheme] = useState(() => (new Cookies().get('theme') === 'light' ? 'light' : 'dark'));
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const [toast, setToast] = useState(null);
	const toastTimer = useRef(null);

	// Persist theme to the app-wide cookie and keep the document id in sync
	// with the convention used by the rest of the client (see Aop.jsx).
	useEffect(() => {
		document.documentElement.id = theme;
		new Cookies().set('theme', theme, { path: '/' });
	}, [theme]);

	useEffect(() => () => clearTimeout(toastTimer.current), []);

	const showToast = useCallback((msg) => {
		setToast(msg);
		clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setToast(null), 3200);
	}, []);

	const setReportPreset = useCallback((preset) => {
		setReportDateRange((prev) => (preset === 'custom' ? { ...prev, preset } : makeReportDateRange(preset)));
	}, []);

	const setReportDate = useCallback((field, value) => {
		setReportDateRange((prev) => {
			const next = { ...prev, preset: 'custom', [field]: value };
			if (next.startDate && next.endDate && next.startDate > next.endDate) {
				if (field === 'startDate') next.endDate = value;
				else next.startDate = value;
			}
			return next;
		});
	}, []);

	const toggleAutopilot = useCallback(() => {
		setAutopilot((v) => {
			showToast(
				v
					? 'Auto-Pilot off — all actions require manual approval'
					: 'Auto-Pilot on — pre-authorized low-risk actions only'
			);
			return !v;
		});
	}, [showToast]);

	// Changing the default preset on the Settings page also re-applies it to the
	// live reporting range so the change is visible immediately.
	const updateDefaultDatePreset = useCallback((preset) => {
		setDefaultDatePreset(preset);
		setReportDateRange(makeReportDateRange(preset === 'custom' ? 'last_90' : preset));
	}, []);

	const updateConfidence = useCallback((key, value) => {
		setConfidence((prev) => ({ ...prev, [key]: value }));
	}, []);

	const navigate = useCallback(
		(nextView, extra = {}) => {
			setSearchParams({ view: nextView, ...extra });
			setMobileNavOpen(false);
			window.scrollTo({ top: 0 });
		},
		[setSearchParams]
	);

	const recordAudit = useCallback(async (partial) => {
		setAuditEvents(await backend.current.recordAudit(partial));
	}, []);

	const actorName = (user?.name || 'operator').split(' ')[0].toLowerCase();

	// ---- Settings: scheduled reports CRUD ----
	const addScheduledReport = useCallback(
		(report) => {
			const id = `sr-${Date.now()}`;
			setScheduledReports((prev) => [{ id, enabled: true, ...report }, ...prev]);
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'scheduled report',
				entity: report.name,
				entity_type: 'report',
				source: 'dashboard',
			});
			showToast(`Scheduled “${report.name}”`);
		},
		[recordAudit, showToast, actorName]
	);

	const updateScheduledReport = useCallback((id, patch) => {
		setScheduledReports((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	}, []);

	const deleteScheduledReport = useCallback(
		(id) => {
			setScheduledReports((prev) => prev.filter((r) => r.id !== id));
			showToast('Scheduled report removed');
		},
		[showToast]
	);

	// ---- Clustering Studio: custom segment CRUD ----
	const createSegment = useCallback(
		(input) => {
			segmentSeq.current += 1;
			const segment = makeCustomSegment(input, segmentSeq.current);
			setSegments((prev) => [segment, ...prev]);
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'created custom segment',
				entity: segment.segment_name,
				entity_type: 'segment',
				source: 'dashboard',
			});
			showToast(`Custom segment “${segment.segment_name}” created`);
			return segment;
		},
		[recordAudit, showToast, actorName]
	);

	const updateSegment = useCallback(
		(id, patch) => {
			const target = segments.find((s) => s.segment_id === id);
			if (!target) return null;
			if (target.is_system_managed) {
				showToast('Recommendation-driven segments are read-only');
				return null;
			}
			const updated = { ...target, ...patch };
			setSegments((prev) => prev.map((s) => (s.segment_id === id ? updated : s)));
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'updated custom segment',
				entity: updated.segment_name,
				entity_type: 'segment',
				source: 'dashboard',
			});
			showToast(`Segment “${updated.segment_name}” updated`);
			return updated;
		},
		[segments, recordAudit, showToast, actorName]
	);

	const deleteSegment = useCallback(
		(id) => {
			const target = segments.find((s) => s.segment_id === id);
			if (!target) return false;
			if (target.is_system_managed) {
				showToast('Recommendation-driven segments cannot be deleted');
				return false;
			}
			setSegments((prev) => prev.filter((s) => s.segment_id !== id));
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'deleted custom segment',
				entity: target.segment_name,
				entity_type: 'segment',
				source: 'dashboard',
			});
			showToast(`Segment “${target.segment_name}” deleted`);
			return true;
		},
		[segments, recordAudit, showToast, actorName]
	);

	// ---- Experiment Lab: custom experiment CRUD ----
	const createExperiment = useCallback(
		(input) => {
			experimentSeq.current += 1;
			const experiment = makeCustomExperiment(input, experimentSeq.current);
			setExperiments((prev) => [experiment, ...prev]);
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'created experiment',
				entity: experiment.experiment_name,
				entity_type: 'experiment',
				source: 'dashboard',
			});
			showToast(`Experiment “${experiment.experiment_name}” created`);
			return experiment;
		},
		[recordAudit, showToast, actorName]
	);

	const updateExperiment = useCallback(
		(id, patch) => {
			const target = experiments.find((e) => e.experiment_id === id);
			if (!target) return null;
			if (target.is_system_managed) {
				showToast('Imported experiments are read-only');
				return null;
			}
			const updated = { ...target, ...patch };
			setExperiments((prev) => prev.map((e) => (e.experiment_id === id ? updated : e)));
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'updated experiment',
				entity: updated.experiment_name,
				entity_type: 'experiment',
				source: 'dashboard',
			});
			showToast(`Experiment “${updated.experiment_name}” updated`);
			return updated;
		},
		[experiments, recordAudit, showToast, actorName]
	);

	const deleteExperiment = useCallback(
		(id) => {
			const target = experiments.find((e) => e.experiment_id === id);
			if (!target) return false;
			if (target.is_system_managed) {
				showToast('Imported experiments cannot be deleted');
				return false;
			}
			setExperiments((prev) => prev.filter((e) => e.experiment_id !== id));
			recordAudit({
				actor_type: 'user',
				actor: actorName,
				action: 'deleted experiment',
				entity: target.experiment_name,
				entity_type: 'experiment',
				source: 'dashboard',
			});
			showToast(`Experiment “${target.experiment_name}” deleted`);
			return true;
		},
		[experiments, recordAudit, showToast, actorName]
	);

	// Staged approval: an approve during internal review advances the action to
	// client review; an approve at the final stage (or reject / needs_changes at
	// any stage) is the final decision. Mirrors the AOP approval service.
	const decideApproval = useCallback(
		async (id, decision) => {
			const actor = (user?.name || 'operator').split(' ')[0].toLowerCase();
			// The staged-approval transition lives in the shared backend (mirrors
			// the AOP approval service); CopApp only adds the audit + toast UI.
			const { approvals: next, target, advanced } = await backend.current.decideApproval(id, decision);
			if (!target) return;
			setApprovals(next);

			if (advanced) {
				recordAudit({
					actor_type: 'user',
					actor,
					action: 'passed internal review for',
					entity: target.justification.split('—')[0].trim().slice(0, 48),
					entity_type: 'approval_request',
					reason: 'Advanced to client approval',
				});
				showToast('Internal approval recorded — awaiting client approval');
				return;
			}

			const verbs = { approved: 'approved', rejected: 'rejected', needs_changes: 'requested changes on' };
			recordAudit({
				actor_type: 'user',
				actor,
				action: `${verbs[decision]} ${target.action_label}`,
				entity: target.justification.split('—')[0].trim().slice(0, 48),
				entity_type: 'approval_request',
				reason: decision === 'approved' ? 'Within policy thresholds' : undefined,
			});
			showToast(
				decision === 'approved'
					? `${target.action_label} approved — queued for launch`
					: decision === 'rejected'
						? `${target.action_label} rejected`
						: 'Changes requested — agent notified'
			);
		},
		[recordAudit, showToast, user]
	);

	// Internal reviewers may edit the action before the client sees it.
	// Audited with a before → after diff, like the AOP edit endpoint.
	const editApproval = useCallback(
		async (id, justification) => {
			const { approvals: next, target, updated } = await backend.current.editApproval(id, justification);
			if (!updated) return;
			setApprovals(next);
			recordAudit({
				actor_type: 'user',
				actor: (user?.name || 'operator').split(' ')[0].toLowerCase(),
				action: 'edited during internal review',
				entity: target.action_label,
				entity_type: 'approval_request',
				diff: { field: 'proposal', before: target.justification.slice(0, 24) + '…', after: justification.slice(0, 24) + '…' },
			});
			showToast('Edit saved — recorded in audit log');
		},
		[recordAudit, showToast, user]
	);

	const submitForApproval = useCallback(
		async (approval) => {
			// COP customer-facing actions default to the full pipeline:
			// internal (Upsellit, edits allowed) -> client -> launch.
			const { approvals: next } = await backend.current.submitForApproval(approval);
			setApprovals(next);
			recordAudit({
				actor_type: 'ai_agent',
				actor: 'campaign_draft_bot',
				action: 'submitted Launch Campaign for approval',
				entity: approval.entity || approval.justification.slice(0, 48),
				entity_type: 'approval_request',
				source: 'worker',
			});
			showToast('Readiness passed — submitted to Approval Queue');
		},
		[recordAudit, showToast]
	);

	const isInternal = viewMode === 'internal';

	// Client View counts only actions awaiting the client's own decision.
	const pendingCount = useMemo(
		() =>
			approvals.filter((a) => a.state === 'submitted' && (isInternal || a.stage === 'client')).length,
		[approvals, isInternal]
	);

	const reportDateRangeLabel = useMemo(() => formatReportDateRange(reportDateRange), [reportDateRange]);

	const ctx = useMemo(
		() => ({
			user,
			approvals,
			pendingCount,
			decideApproval,
			editApproval,
			submitForApproval,
			auditEvents,
			recordAudit,
			segments,
			createSegment,
			updateSegment,
			deleteSegment,
			experiments,
			createExperiment,
			updateExperiment,
			deleteExperiment,
			autopilot,
			toggleAutopilot,
			autopilotRiskCeiling,
			setAutopilotRiskCeiling,
			confidence,
			updateConfidence,
			scheduledReports,
			addScheduledReport,
			updateScheduledReport,
			deleteScheduledReport,
			suppressions,
			setSuppressions,
			brandGuide,
			setBrandGuide,
			defaultAllocationMode,
			setDefaultAllocationMode,
			defaultExperimentLength,
			setDefaultExperimentLength,
			defaultConfidencePct,
			setDefaultConfidencePct,
			defaultDatePreset,
			updateDefaultDatePreset,
			reportDatePresets: REPORT_DATE_PRESETS,
			navigate,
			showToast,
			builderSegment,
			isInternal,
			reportDateRange,
			reportDateRangeLabel,
		}),
		[user, approvals, pendingCount, decideApproval, editApproval, submitForApproval, auditEvents, recordAudit, segments, createSegment, updateSegment, deleteSegment, experiments, createExperiment, updateExperiment, deleteExperiment, autopilot, toggleAutopilot, autopilotRiskCeiling, confidence, updateConfidence, scheduledReports, addScheduledReport, updateScheduledReport, deleteScheduledReport, suppressions, brandGuide, defaultAllocationMode, defaultExperimentLength, defaultConfidencePct, defaultDatePreset, updateDefaultDatePreset, navigate, showToast, builderSegment, isInternal, reportDateRange, reportDateRangeLabel]
	);

	const ViewComponent = VIEWS[view] || CommandCenter;
	const showReportDateRange = REPORTING_DATE_VIEWS.has(view);

	return (
		<CopContext.Provider value={ctx}>
			<div className={`cop-root${theme === 'light' ? ' light' : ''}`}>
				<CopSidebar
					view={view}
					onNavigate={navigate}
					pendingCount={pendingCount}
					open={mobileNavOpen}
					onClose={() => setMobileNavOpen(false)}
					viewMode={viewMode}
					onSetViewMode={(mode) => {
						setViewMode(mode);
						showToast(
							mode === 'client'
								? 'Client View — exactly what the account sees in Client Reporting'
								: 'Internal View — ops telemetry, warnings, and AOP links visible'
						);
					}}
				/>
				<div className="cop-main">
					<CopTopbar
						view={view}
						isInternal={isInternal}
						autopilot={autopilot}
						user={user}
						theme={theme}
						onSetTheme={setTheme}
						onNavigate={navigate}
						onMenuClick={() => setMobileNavOpen(true)}
						reportDateRange={showReportDateRange ? reportDateRange : null}
						reportDateRangePresets={REPORT_DATE_PRESETS}
						onSetReportPreset={setReportPreset}
						onSetReportDate={setReportDate}
					/>
					<ViewComponent key={view} />
				</div>
				{toast && <Toast>{toast}</Toast>}
			</div>
		</CopContext.Provider>
	);
}
